import React, { useState, useEffect } from 'react';
import RegisterModal from './RegisterModal';
import './HomePage.css';

const FEATURES = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" width="28" height="28">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="#C8102E" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'AI Chatbot',
    desc: 'Instant, accurate answers to guest questions — powered by GPT, trained on your property.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" width="28" height="28">
        <rect x="3" y="3" width="7" height="7" rx="1" stroke="#C8102E" strokeWidth="1.8"/>
        <rect x="14" y="3" width="7" height="7" rx="1" stroke="#C8102E" strokeWidth="1.8"/>
        <rect x="3" y="14" width="7" height="7" rx="1" stroke="#C8102E" strokeWidth="1.8"/>
        <path d="M14 14h2v2h-2zM18 14h3v3h-3zM14 18h3v3h-3zM19 19h2v2h-2z" fill="#C8102E"/>
      </svg>
    ),
    title: 'QR Code Access',
    desc: 'Guests scan a QR code on arrival — no app download, no account needed.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" width="28" height="28">
        <path d="M5 12.55a11 11 0 0114.08 0M1.42 9a16 16 0 0121.16 0M8.53 16.11a6 6 0 016.95 0M12 20h.01" stroke="#C8102E" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'WiFi & Local Info',
    desc: 'Store WiFi credentials, nearby attractions and restaurants — surfaced automatically.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" width="28" height="28">
        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" stroke="#C8102E" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Unanswered Alerts',
    desc: "Get email alerts when a guest asks something the AI couldn't answer — never miss a thing.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" width="28" height="28">
        <rect x="3" y="3" width="7" height="7" rx="1" stroke="#C8102E" strokeWidth="1.8"/>
        <rect x="14" y="3" width="7" height="7" rx="1" stroke="#C8102E" strokeWidth="1.8"/>
        <rect x="3" y="14" width="7" height="7" rx="1" stroke="#C8102E" strokeWidth="1.8"/>
        <rect x="14" y="14" width="7" height="7" rx="1" stroke="#C8102E" strokeWidth="1.8"/>
      </svg>
    ),
    title: 'Property Dashboard',
    desc: 'Manage all your properties, guidebooks, and chat sessions from one place.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" width="28" height="28">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="#C8102E" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        <polyline points="9 22 9 12 15 12 15 22" stroke="#C8102E" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Multi-Property',
    desc: 'Run multiple Airbnb properties? Each gets its own chatbot, guidebook, and QR code.',
  },
];

const CHAT_MESSAGES = [
  { role: 'user', text: "What's the WiFi password?" },
  { role: 'ai',   text: 'WiFi: VillaGuest_5G · Password: Welcome2024! 🏡' },
  { role: 'user', text: 'What time is check-out?' },
  { role: 'ai',   text: 'Check-out is at 11:00 AM. Late check-out may be available on request!' },
];

const FORUM_POSTS = [
  { cat: 'Tips & Tricks', catColor: '#7c3aed', title: 'How do I handle late check-out requests through the chatbot?', author: 'Sarah M.', initials: 'SM', replies: 14, time: '2h ago' },
  { cat: 'Guides',        catColor: '#0891b2', title: 'Best practices for writing a guidebook guests actually read', author: 'James K.', initials: 'JK', replies: 31, time: '5h ago' },
  { cat: 'Help',          catColor: '#C8102E', title: 'WiFi keeps dropping — what should I put in the guidebook?', author: 'Priya R.', initials: 'PR', replies: 8,  time: '1d ago' },
  { cat: 'Feature Ideas', catColor: '#059669', title: 'Multi-language support for international guests — anyone tried this?', author: 'Lucas F.', initials: 'LF', replies: 22, time: '1d ago' },
  { cat: 'Guides',        catColor: '#0891b2', title: '5 things to always include in your check-in instructions', author: 'Anna T.', initials: 'AT', replies: 47, time: '3d ago' },
  { cat: 'Help',          catColor: '#C8102E', title: 'Unanswered alerts going to spam — how to fix?', author: 'Mark D.', initials: 'MD', replies: 6,  time: '4d ago' },
];

export default function HomePage() {
  const [scrolled, setScrolled]           = useState(false);
  const [visibleMsgs, setVisibleMsgs]     = useState(1);
  const [showRegister, setShowRegister]   = useState(false);
  const [contact, setContact]             = useState({ name: '', email: '', message: '' });
  const [contactStatus, setContactStatus] = useState('idle'); // idle | sending | sent

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (visibleMsgs >= CHAT_MESSAGES.length) return;
    const t = setTimeout(() => setVisibleMsgs(v => v + 1), 900);
    return () => clearTimeout(t);
  }, [visibleMsgs]);

  const goToLogin = () => { window.location.href = '/?admin=1'; };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleContactSend = (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.message) return;
    setContactStatus('sending');
    setTimeout(() => {
      setContactStatus('sent');
      setContact({ name: '', email: '', message: '' });
    }, 1200);
  };

  return (
    <div className="hp-root">

      {showRegister && (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onLoginRedirect={() => { setShowRegister(false); goToLogin(); }}
        />
      )}

      {/* ── Navbar ── */}
      <nav className={`hp-nav${scrolled ? ' hp-nav--solid' : ''}`}>
        <div className="hp-nav-inner">
          <div className="hp-logo">
            <svg viewBox="0 0 24 24" fill="none" width="28" height="28">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" fill="#C8102E" fillOpacity=".15" stroke="#C8102E" strokeWidth="1.8" strokeLinejoin="round"/>
              <path d="M9 22V12h6v10" stroke="#C8102E" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            <span className="hp-logo-text">whereitis</span>
          </div>
          <div className="hp-nav-links">
            <button className="hp-nav-link" onClick={() => scrollTo('features')}>Features</button>
            <button className="hp-nav-link" onClick={() => scrollTo('community')}>Community</button>
            <button className="hp-nav-link" onClick={() => scrollTo('contact')}>Contact</button>
          </div>
          <button className="hp-nav-login" onClick={goToLogin}>Admin Login</button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="hp-hero">
        <div className="hp-hero-left">
          <div className="hp-chip">AI · PROPERTY MANAGEMENT</div>
          <h1 className="hp-hero-h1">
            Your Airbnb,<br />
            <span className="hp-hero-accent">Answered Instantly</span>
          </h1>
          <p className="hp-hero-sub">
            Set up an AI assistant for your property. Guests scan a QR code
            and get instant answers — WiFi, check-in, local tips — 24/7,
            no calls needed.
          </p>
          <div className="hp-hero-btns">
            <button className="hp-btn-primary" onClick={() => setShowRegister(true)}>Get Started Free</button>
            <button className="hp-btn-outline" onClick={() => scrollTo('features')}>See Features</button>
          </div>
        </div>

        <div className="hp-hero-right">
          <div className="hp-phone">
            <div className="hp-phone-bar">
              <div className="hp-phone-dot" />
              <div className="hp-phone-dot" />
              <div className="hp-phone-dot" />
            </div>
            <div className="hp-phone-header">
              <div className="hp-phone-avatar">M</div>
              <div>
                <div className="hp-phone-title">Mountain View Villa</div>
                <div className="hp-phone-status">AI Assistant · Online</div>
              </div>
            </div>
            <div className="hp-phone-messages">
              {CHAT_MESSAGES.slice(0, visibleMsgs).map((m, i) => (
                <div key={i} className={`hp-msg hp-msg--${m.role}${i === visibleMsgs - 1 ? ' hp-msg--new' : ''}`}>
                  {m.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="hp-stats">
        <div className="hp-stats-inner">
          {[
            { num: '2 min', label: 'Setup time' },
            { num: '24/7',  label: 'Guest availability' },
            { num: '100%',  label: 'Questions automated' },
          ].map(s => (
            <div className="hp-stat" key={s.label}>
              <span className="hp-stat-num">{s.num}</span>
              <span className="hp-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Pain Points ── */}
      <section className="hp-pain">
        <div className="hp-section-inner">
          <p className="hp-section-tag">THE PROBLEM</p>
          <h2 className="hp-section-h2">Tired of Repeating Yourself?</h2>
          <p className="hp-pain-sub">
            Every host knows the feeling. The same messages arrive at midnight, on weekends,
            during dinner — every single check-in. Your AI assistant handles them all.
          </p>
          <div className="hp-pain-grid">
            {[
              "What's the WiFi password?",
              "How do I check in?",
              "Where can I park?",
              "What time is checkout?",
              "Any good restaurants nearby?",
              "How does the heating work?",
              "Is there a hair dryer?",
              "How do I use the washing machine?",
            ].map(q => (
              <div className="hp-pain-card" key={q}>
                <span className="hp-pain-q">"{q}"</span>
                <span className="hp-pain-badge">AI handles this</span>
              </div>
            ))}
          </div>
          <p className="hp-pain-bottom">
            whereitis answers all of these — <strong>instantly, accurately, 24/7</strong> — so you don't have to.
          </p>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="hp-how">
        <div className="hp-section-inner">
          <p className="hp-section-tag">SIMPLE SETUP</p>
          <h2 className="hp-section-h2">How It Works</h2>
          <div className="hp-steps">
            {[
              { n: '1', title: 'Create Your Guidebook', desc: 'Add WiFi details, check-in instructions, house rules, and local recommendations in minutes.' },
              { n: '2', title: 'Share Your QR Code',    desc: 'Print and place the QR code in your property. Guests scan it on arrival — no app needed.' },
              { n: '3', title: 'Guests Get Answers',    desc: "Your AI assistant handles questions 24/7. You get alerts only when it can't answer." },
            ].map((step, i, arr) => (
              <React.Fragment key={step.n}>
                <div className="hp-step">
                  <div className="hp-step-num">{step.n}</div>
                  <h3 className="hp-step-title">{step.title}</h3>
                  <p className="hp-step-desc">{step.desc}</p>
                </div>
                {i < arr.length - 1 && <div className="hp-step-arrow">→</div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="hp-features" id="features">
        <div className="hp-section-inner">
          <p className="hp-section-tag">FEATURES</p>
          <h2 className="hp-section-h2">Everything You Need</h2>
          <div className="hp-feature-grid">
            {FEATURES.map(f => (
              <div className="hp-feature-card" key={f.title}>
                <div className="hp-feature-icon">{f.icon}</div>
                <h3 className="hp-feature-title">{f.title}</h3>
                <p className="hp-feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Hassle-Free Setup ── */}
      <section className="hp-setup">
        <div className="hp-setup-inner">
          <div className="hp-setup-left">
            <p className="hp-section-tag" style={{ textAlign: 'left' }}>ZERO HASSLE</p>
            <h2 className="hp-setup-h2">
              Up and Running<br />
              <span className="hp-hero-accent">in Under 2 Minutes</span>
            </h2>
            <p className="hp-setup-sub">
              No developers. No technical knowledge. No app installs.
              If you can fill in a form, you can set up whereitis.
            </p>
            <ul className="hp-setup-list">
              {[
                { title: 'Write your guidebook once', desc: 'Add WiFi, check-in steps, house rules, local tips in plain English — no special format required.' },
                { title: 'AI learns your property instantly', desc: 'No training period. Your chatbot is ready the moment you save.' },
                { title: 'One QR code, forever', desc: 'Print it, frame it, stick it on the fridge. Works for every guest, every stay.' },
                { title: 'Update anytime', desc: "Changed your WiFi? New house rule? Edit your guidebook and it's live in seconds." },
                { title: 'Works on any device', desc: 'Guests scan with their own phone — no download, no account, no friction.' },
              ].map(item => (
                <li className="hp-setup-item" key={item.title}>
                  <span className="hp-setup-check">✓</span>
                  <div>
                    <strong>{item.title}</strong>
                    <p>{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="hp-setup-right">
            <div className="hp-setup-card">
              <div className="hp-setup-card-header">
                <span className="hp-setup-card-dot" style={{ background: '#ef4444' }} />
                <span className="hp-setup-card-dot" style={{ background: '#f59e0b' }} />
                <span className="hp-setup-card-dot" style={{ background: '#22c55e' }} />
                <span className="hp-setup-card-title">Guidebook Editor</span>
              </div>
              <div className="hp-setup-form">
                <div className="hp-form-field">
                  <label className="hp-form-label">Property Name</label>
                  <div className="hp-form-input">Mountain View Villa</div>
                </div>
                <div className="hp-form-row">
                  <div className="hp-form-field">
                    <label className="hp-form-label">WiFi Name</label>
                    <div className="hp-form-input">VillaGuest_5G</div>
                  </div>
                  <div className="hp-form-field">
                    <label className="hp-form-label">WiFi Password</label>
                    <div className="hp-form-input">Welcome2024!</div>
                  </div>
                </div>
                <div className="hp-form-field">
                  <label className="hp-form-label">Check-in Instructions</label>
                  <div className="hp-form-textarea">
                    Key is in the lockbox at the front door. Code: 4821.
                    Please arrive after 3 PM. Make yourself at home!
                  </div>
                </div>
                <div className="hp-form-field">
                  <label className="hp-form-label">Nearby Restaurants</label>
                  <div className="hp-form-input">Bella Italia (5 min), The Grill House (10 min)...</div>
                </div>
                <div className="hp-form-field">
                  <label className="hp-form-label">House Rules</label>
                  <div className="hp-form-input">No smoking · No pets · Quiet after 10 PM</div>
                </div>
                <div className="hp-setup-form-footer">
                  <span className="hp-setup-form-hint">✓ AI chatbot ready immediately after saving</span>
                  <button className="hp-form-save">Save &amp; Publish →</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Community Forum ── */}
      <section className="hp-community" id="community">
        <div className="hp-section-inner">
          <p className="hp-section-tag">COMMUNITY</p>
          <h2 className="hp-section-h2">Property Managers Helping Each Other</h2>
          <p className="hp-community-sub">
            Join hundreds of hosts sharing tips, guides, and solutions. Ask questions,
            share what works, and learn from real experiences.
          </p>
          <div className="hp-forum-grid">
            {FORUM_POSTS.map(post => (
              <div className="hp-forum-card" key={post.title}>
                <div className="hp-forum-top">
                  <span className="hp-forum-cat" style={{ color: post.catColor, background: `${post.catColor}14`, borderColor: `${post.catColor}33` }}>
                    {post.cat}
                  </span>
                </div>
                <p className="hp-forum-title">{post.title}</p>
                <div className="hp-forum-footer">
                  <div className="hp-forum-author">
                    <div className="hp-forum-avatar">{post.initials}</div>
                    <span className="hp-forum-name">{post.author}</span>
                  </div>
                  <div className="hp-forum-meta">
                    <span className="hp-forum-replies">
                      <svg viewBox="0 0 16 16" fill="none" width="13" height="13">
                        <path d="M14 1H2a1 1 0 00-1 1v8a1 1 0 001 1h3l3 4 3-4h3a1 1 0 001-1V2a1 1 0 00-1-1z" stroke="currentColor" strokeWidth="1.4"/>
                      </svg>
                      {post.replies}
                    </span>
                    <span className="hp-forum-time">{post.time}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="hp-community-actions">
            <button className="hp-btn-primary" onClick={() => setShowRegister(true)}>Join the Community</button>
            <span className="hp-community-hint">Free for all whereitis users</span>
          </div>
        </div>
      </section>

      {/* ── Contact Us ── */}
      <section className="hp-contact" id="contact">
        <div className="hp-contact-inner">

          <div className="hp-contact-left">
            <p className="hp-section-tag" style={{ textAlign: 'left' }}>CONTACT US</p>
            <h2 className="hp-contact-h2">We're Here to Help</h2>
            <p className="hp-contact-sub">
              Have a question, feature request, or just want to say hi?
              Drop us a message and we'll get back to you within 24 hours.
            </p>
            <div className="hp-contact-info">
              <div className="hp-contact-info-item">
                <div className="hp-contact-info-icon">
                  <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#C8102E" strokeWidth="1.8"/>
                    <polyline points="22,6 12,13 2,6" stroke="#C8102E" strokeWidth="1.8"/>
                  </svg>
                </div>
                <div>
                  <div className="hp-contact-info-label">Email</div>
                  <div className="hp-contact-info-value">support@whereitis.ai</div>
                </div>
              </div>
              <div className="hp-contact-info-item">
                <div className="hp-contact-info-icon">
                  <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
                    <circle cx="12" cy="12" r="10" stroke="#C8102E" strokeWidth="1.8"/>
                    <polyline points="12 6 12 12 16 14" stroke="#C8102E" strokeWidth="1.8"/>
                  </svg>
                </div>
                <div>
                  <div className="hp-contact-info-label">Response Time</div>
                  <div className="hp-contact-info-value">Within 24 hours</div>
                </div>
              </div>
              <div className="hp-contact-info-item">
                <div className="hp-contact-info-icon">
                  <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="#C8102E" strokeWidth="1.8"/>
                  </svg>
                </div>
                <div>
                  <div className="hp-contact-info-label">Community</div>
                  <div className="hp-contact-info-value">Join 500+ property managers</div>
                </div>
              </div>
            </div>
          </div>

          <div className="hp-contact-right">
            <div className="hp-mailbox">
              <div className="hp-mailbox-header">
                <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="1.6"/>
                  <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="1.6"/>
                </svg>
                <span>New Message</span>
              </div>

              {contactStatus === 'sent' ? (
                <div className="hp-mailbox-sent">
                  <div className="hp-mailbox-sent-icon">
                    <svg viewBox="0 0 24 24" fill="none" width="36" height="36">
                      <circle cx="12" cy="12" r="10" stroke="#22c55e" strokeWidth="1.8"/>
                      <polyline points="8 12 11 15 16 9" stroke="#22c55e" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h3>Message Sent!</h3>
                  <p>Thanks for reaching out. We'll get back to you within 24 hours.</p>
                  <button className="hp-mailbox-reset" onClick={() => setContactStatus('idle')}>Send another message</button>
                </div>
              ) : (
                <form className="hp-mailbox-form" onSubmit={handleContactSend} noValidate>
                  <div className="hp-mailbox-to">
                    <span className="hp-mailbox-to-label">To:</span>
                    <span className="hp-mailbox-to-value">support@whereitis.ai</span>
                  </div>
                  <div className="hp-mailbox-fields">
                    <div className="hp-mbox-row">
                      <div className="hp-mbox-field">
                        <label className="hp-mbox-label">Your Name</label>
                        <input
                          className="hp-mbox-input"
                          type="text"
                          placeholder="John Smith"
                          value={contact.name}
                          onChange={e => setContact(p => ({ ...p, name: e.target.value }))}
                        />
                      </div>
                      <div className="hp-mbox-field">
                        <label className="hp-mbox-label">Email</label>
                        <input
                          className="hp-mbox-input"
                          type="email"
                          placeholder="john@example.com"
                          value={contact.email}
                          onChange={e => setContact(p => ({ ...p, email: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="hp-mbox-field">
                      <label className="hp-mbox-label">Message</label>
                      <textarea
                        className="hp-mbox-textarea"
                        placeholder="Tell us how we can help..."
                        rows={5}
                        value={contact.message}
                        onChange={e => setContact(p => ({ ...p, message: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="hp-mailbox-footer">
                    <span className="hp-mailbox-footer-hint">We read every message</span>
                    <button
                      type="submit"
                      className="hp-mailbox-send"
                      disabled={contactStatus === 'sending' || !contact.name || !contact.email || !contact.message}
                    >
                      {contactStatus === 'sending' ? (
                        <span className="hp-mailbox-spinner" />
                      ) : (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
                            <line x1="22" y1="2" x2="11" y2="13" stroke="currentColor" strokeWidth="2"/>
                            <polygon points="22 2 15 22 11 13 2 9 22 2" stroke="currentColor" strokeWidth="2" fill="none"/>
                          </svg>
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="hp-cta">
        <div className="hp-cta-inner">
          <h2 className="hp-cta-h2">Ready to upgrade your Airbnb?</h2>
          <p className="hp-cta-sub">Give your guests instant answers and save hours of repetitive messages.</p>
          <button className="hp-btn-white" onClick={() => setShowRegister(true)}>Get Started Free</button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="hp-footer">
        <span>© 2026 whereitis</span>
        <span className="hp-footer-sep">·</span>
        <button className="hp-footer-link" onClick={() => scrollTo('features')}>Features</button>
        <span className="hp-footer-sep">·</span>
        <button className="hp-footer-link" onClick={() => scrollTo('community')}>Community</button>
        <span className="hp-footer-sep">·</span>
        <button className="hp-footer-link" onClick={() => scrollTo('contact')}>Contact</button>
        <span className="hp-footer-sep">·</span>
        <button className="hp-footer-link" onClick={goToLogin}>Admin Login</button>
      </footer>
    </div>
  );
}
